import { UserCredential } from "firebase/auth";

import { UserType } from "@seneca/user-service-consts";

import { parseFirebaseUserDisplayName } from "services/firebase/auth";
import {
  goStudentProvider,
  studienkreisProvider
} from "services/firebase/auth/ssoProvider";

import { removeUndefinedKeys } from "seneca-common/utils/functions";

import { FirebaseUserInfo } from "../types";

type FirebaseUserDetails = {
  email: string;
  userId?: string;
  phoneNumber?: string;
  birthdate?: string;
  gender?: string;
  givenName?: string;
  familyName?: string;
};

type CommonUserDetails = FirebaseUserDetails & {
  type?: UserType;
};

type GoStudentUserDetails = FirebaseUserDetails &
  (
    | {
        type: UserType.teacher;
        role: "tutor";
      }
    | {
        type: UserType.student;
      }
  );

export type UserDetails = CommonUserDetails | GoStudentUserDetails;

export default function extractUserDetails({
  authInfo,
  userProfile
}: {
  authInfo: UserCredential;
  userProfile?: FirebaseUserInfo["profile"];
}): UserDetails {
  if (authInfo.providerId === goStudentProvider.providerId && userProfile) {
    return extractGoStudentInfo({ authInfo, userProfile });
  }

  if (authInfo.providerId === studienkreisProvider.providerId) {
    return extractStudienkreisInfo({ authInfo });
  }

  return extractSenecaUserDetails(authInfo);
}

function extractSenecaUserDetails(authInfo: UserCredential) {
  const { displayName, email } = authInfo.user;
  const { givenName, familyName } = parseFirebaseUserDisplayName(displayName);

  if (!email) {
    throw new Error("Email is required");
  }

  return {
    email,
    givenName,
    familyName
  };
}

function isTutorRole(roles: string[]): boolean {
  return roles.includes("TUTOR");
}

function extractGoStudentInfo({
  authInfo,
  userProfile
}: {
  authInfo: UserCredential;
  userProfile: FirebaseUserInfo["profile"];
}): GoStudentUserDetails {
  const { displayName } = authInfo.user;
  const { givenName, familyName } = parseFirebaseUserDisplayName(displayName);

  const userId = authInfo.user.uid;
  const birthdate = userProfile.birthdate;
  const phoneNumber = userProfile.extra_info.phone_number;
  const roles = userProfile.extra_info.roles;
  const gender = userProfile.extra_info.gender;
  const email = `${userId}@gostudentuser.com`;

  const isTutor = isTutorRole(roles);

  return removeUndefinedKeys({
    userId,
    birthdate,
    phoneNumber,
    gender,
    email,
    givenName,
    familyName,
    type: isTutor ? UserType.teacher : UserType.student,
    role: isTutor ? "tutor" : undefined
  }) as GoStudentUserDetails;
}

function extractStudienkreisInfo({
  authInfo
}: {
  authInfo: UserCredential;
}): CommonUserDetails {
  const { displayName } = authInfo.user;
  const { givenName, familyName } = parseFirebaseUserDisplayName(displayName);

  const userId = authInfo.user.uid;
  const email = `${userId}@studienkreisuser.com`;

  return removeUndefinedKeys({
    userId,
    email,
    givenName,
    familyName,
    type: UserType.student
  }) as CommonUserDetails;
}
